import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface AssistantContextEntry<T = unknown> {
  summary: string;
  data?: T;
  source?: string;
  updatedAt?: number;
  maxSerializedLength?: number | null;
  /**
   * Values the chat UI needs locally. Omitted from the serialized assistant payload.
   */
  clientState?: {
    rawCurrentInput?: unknown;
  };
}

export type AssistantContextSnapshot = Record<string, AssistantContextEntry>;

interface AssistantContextUpdater {
  setContext: (key: string, entry: AssistantContextEntry | null) => void;
}

const AssistantContextStateContext = createContext<AssistantContextSnapshot>({});
const AssistantContextUpdaterContext = createContext<AssistantContextUpdater | null>(null);

const entriesAreEqual = (prev: AssistantContextEntry | undefined, next: AssistantContextEntry): boolean => {
  if (!prev) {
    return false;
  }

  return (
    prev.summary === next.summary &&
    prev.source === next.source &&
    prev.data === next.data &&
    prev.maxSerializedLength === next.maxSerializedLength &&
    prev.clientState === next.clientState
  );
};

export const AssistantContextProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [contexts, setContexts] = useState<AssistantContextSnapshot>({});

  const setContext = useCallback((key: string, entry: AssistantContextEntry | null) => {
    setContexts(prev => {
      if (entry === null) {
        if (!(key in prev)) {
          return prev;
        }
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }

      const nextEntry: AssistantContextEntry = {
        ...entry,
        updatedAt: Date.now(),
      };

      if (entriesAreEqual(prev[key], nextEntry)) {
        return prev;
      }

      return {
        ...prev,
        [key]: nextEntry,
      };
    });
  }, []);

  const updater = useMemo<AssistantContextUpdater>(() => ({ setContext }), [setContext]);

  return (
    <AssistantContextUpdaterContext.Provider value={updater}>
      <AssistantContextStateContext.Provider value={contexts}>
        {children}
      </AssistantContextStateContext.Provider>
    </AssistantContextUpdaterContext.Provider>
  );
};

export const useAssistantContextSnapshot = (): AssistantContextSnapshot => {
  return useContext(AssistantContextStateContext);
};

export const useAssistantContextSetter = (): AssistantContextUpdater['setContext'] => {
  const context = useContext(AssistantContextUpdaterContext);
  if (!context) {
    throw new Error('useAssistantContextSetter must be used within an AssistantContextProvider');
  }
  return context.setContext;
};

export const useAssistantContextPublisher = <T,>(key: string, entry: AssistantContextEntry<T> | null) => {
  const setContext = useAssistantContextSetter();

  useEffect(() => {
    setContext(key, entry as AssistantContextEntry | null);
    return () => {
      setContext(key, null);
    };
  }, [key, entry, setContext]);
};

